'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildGrumftScriptSrc,
  getGamZone,
  GRUMFT_IAB_PRESETS,
  isGrumftScriptTagsEnabled,
} from '../../../lib/grumft-iab-presets';
import { showGamSlot } from '../../../lib/gam-gpt';
import {
  AD_DEFER_IDLE_IAB_MS,
  scheduleDeferredAdLoad,
} from '../../../lib/ad-script-defer';

export { GRUMFT_IAB_PRESETS, buildGrumftScriptSrc };

const DEFAULT_CONTAINER_ID = GRUMFT_IAB_PRESETS.categorySidebar.containerId;
const DEFAULT_ZONE = GRUMFT_IAB_PRESETS.categorySidebar.grumftZone;
export const GRUMFT_DEFAULT_INTERSECTION_ROOT_MARGIN = '200px 0px';

export function injectGrumftScriptIntoSlot(src, anchorEl) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  script.setAttribute('data-ad-deferred', '1');
  script.setAttribute('data-no-optimize', '1');
  anchorEl.appendChild(script);
  const loaded = new Promise((resolve, reject) => {
    script.onload = () => {
      script.setAttribute('data-grumft-loaded', '1');
      resolve();
    };
    script.onerror = () => {
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(new Error('Grumft: script falhou'));
    };
  });
  return { script, loaded };
}

/**
 * Slot Grumft IAB 336×280 — mesmo padrão do tvfoco-frontend (lazy + idle).
 * Por padrão chama o GAM direto (gam-gpt.js); NEXT_PUBLIC_GRUMFT_TAGS=true volta ao script deles.
 */
export default function GrumftIabAd({
  containerId = DEFAULT_CONTAINER_ID,
  grumftZone = DEFAULT_ZONE,
  scriptSrc: scriptSrcProp,
  label = 'Continua depois da publicidade',
  showLabel = true,
  slotHeightPx = 280,
  height: heightProp,
  rootMargin = GRUMFT_DEFAULT_INTERSECTION_ROOT_MARGIN,
  loadTrigger = 'intersection',
  className = '',
  visualVariant = 'default',
}) {
  const isPostVisualVariant = visualVariant === 'post';
  const pixelHeight =
    heightProp != null && Number.isFinite(Number(heightProp))
      ? Number(heightProp)
      : slotHeightPx;

  const slotContainerRef = useRef(null);
  const injectedScriptRef = useRef(null);
  const gamSlotRef = useRef(null);
  const [loadState, setLoadState] = useState('idle');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const scriptSrc = useMemo(() => {
    if (scriptSrcProp && String(scriptSrcProp).trim()) {
      return String(scriptSrcProp).trim();
    }
    if (!isGrumftScriptTagsEnabled()) return '';
    return buildGrumftScriptSrc(grumftZone);
  }, [scriptSrcProp, grumftZone]);
  const gamZone = useMemo(
    () => (scriptSrc ? null : getGamZone(grumftZone)),
    [scriptSrc, grumftZone]
  );

  useLayoutEffect(() => {
    if (!scriptSrc && !gamZone) {
      setLoadState('error');
      return undefined;
    }

    const el = slotContainerRef.current;
    if (!el) return undefined;

    let scriptCancelled = false;
    let cancelDefer = () => {};

    const executeStartLoad = () => {
      setLoadState('loading');
      const anchor = slotContainerRef.current;
      if (!anchor) return;
      let loaded;
      if (gamZone) {
        const gam = showGamSlot(anchor, gamZone);
        gamSlotRef.current = gam;
        loaded = gam.loaded;
      } else {
        const injected = injectGrumftScriptIntoSlot(scriptSrc, anchor);
        injectedScriptRef.current = injected.script;
        loaded = injected.loaded;
      }
      loaded
        .then(() => {
          if (!scriptCancelled) setLoadState('loaded');
        })
        .catch(() => {
          if (!scriptCancelled) setLoadState('error');
        });
    };

    const queueStartLoad = () => {
      cancelDefer();
      cancelDefer = scheduleDeferredAdLoad(
        () => {
          if (!scriptCancelled) executeStartLoad();
        },
        { idleTimeout: AD_DEFER_IDLE_IAB_MS }
      );
    };

    const removeInjectedScript = () => {
      const s = injectedScriptRef.current;
      injectedScriptRef.current = null;
      if (s?.parentNode) s.parentNode.removeChild(s);
      gamSlotRef.current?.destroy();
      gamSlotRef.current = null;
    };

    if (loadTrigger === 'scroll') {
      const listenerOpts = { passive: true };
      const onFirstScroll = () => {
        window.removeEventListener('scroll', onFirstScroll, listenerOpts);
        queueStartLoad();
      };
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0) {
        queueStartLoad();
      } else {
        window.addEventListener('scroll', onFirstScroll, listenerOpts);
      }
      return () => {
        scriptCancelled = true;
        cancelDefer();
        window.removeEventListener('scroll', onFirstScroll, listenerOpts);
        removeInjectedScript();
      };
    }

    if (typeof IntersectionObserver === 'undefined') {
      queueStartLoad();
      return () => {
        scriptCancelled = true;
        cancelDefer();
        removeInjectedScript();
      };
    }

    let observerDone = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible || observerDone) return;
        observerDone = true;
        observer.disconnect();
        queueStartLoad();
      },
      { root: null, rootMargin, threshold: 0 }
    );
    observer.observe(el);

    return () => {
      scriptCancelled = true;
      cancelDefer();
      observer.disconnect();
      removeInjectedScript();
    };
  }, [scriptSrc, gamZone, rootMargin, loadTrigger]);

  const labelBlock =
    showLabel && label ? (
      isPostVisualVariant ? (
        <div className="grumft-ad-label grumft-ad-label--post">
          <span>{label}</span>
        </div>
      ) : (
        <span className="grumft-ad-label">{label}</span>
      )
    ) : null;

  return (
    <div className={`grumft-ad ${className}`.trim()}>
      {labelBlock}
      <div
        ref={slotContainerRef}
        id={containerId}
        className="grumft-ad-slot"
        style={{ height: `${pixelHeight}px`, minHeight: `${pixelHeight}px` }}
        aria-hidden={loadState === 'error'}
      >
        {(loadState === 'idle' || loadState === 'loading') && hydrated ? (
          <div className="grumft-ad-skeleton" aria-hidden />
        ) : null}
        {loadState === 'error' ? (
          <div className="grumft-ad-error" role="status">
            Publicidade indisponível no momento
          </div>
        ) : null}
      </div>
    </div>
  );
}
